// src/pages/platform.ts —— 06 C-20 公告·工单·FAQ / C-21 管理员账号页定义（PAGES_PLATFORM 经 pages/index 注册，后序覆盖 PAGES_SHELLS 同名搭壳页）、
// C-21 重置 2FA 出口（resetTwoFa / canResetTwoFa），以及 06 C-13 products 页风控两页签视图（riskViews，由 pages/products.ts 引用；products 页仍由 PAGES_PRODUCTS 承载）。
// C-22 角色页为 dash 原生页（domains/dash.roles.ts）。
import { ApiError } from '../../../shared/src/api/client';
import type { Dict } from '../../../shared/src/dc';
import { interpolate } from '../../../shared/src/errors';
import { fmtDateTime } from '../../../shared/src/time';
import { closeDrawer, openConfirm, openDrawer, openForm, reloadCurrent, sessionGuard, tableOf, viewOf } from '../domains/table';
import { str, strArr, type AdminCtx } from '../domains/types';
import { I18N_PLATFORM, REWRITES_PLATFORM } from '../i18n/platform';
import { SUPER_ADMIN_ROLE } from '../perm';
import type { AcctSummary, AdminAcctRow, ContentDetail, ContentRow, LoginLogRow, RiskHitRow, RiskRuleRow, RoleRow } from './platform.types';
import { openUser360 } from './shells';
import { defineView, type ActionDef, type DrawerAction, type DrawerSpec, type ErasedView, type FieldDef, type FormDef, type FormVals, type Need, type PageMap } from './types';
import { cell, chip, chipOf, localErr, type CellVals, type ChipKind } from './ui';

const EMPTY = '—';

// ---- 公共 ----

// 启用 facet（enabled 1 / 0）选项文案：卡段与风控两视图共用（06 C-13）；函数声明随模块实例化即可用（products.ts 加载环中不触发 TDZ）
export function enabledLabel(t: Dict, v: string): string {
  return v === '1' ? str(t, 'stOn') || v : v === '0' ? str(t, 'stOff') || v : v;
}

function dt(ctx: AdminCtx, iso: string | null | undefined): string {
  return fmtDateTime(iso, { tz: ctx.host.state.sh.timezone || undefined });
}

function txt(v: FormVals, k: string): string {
  const x = v[k];
  return typeof x === 'string' ? x.trim() : '';
}

function reasonOf(e: unknown): string {
  return e instanceof ApiError && e.code === 1012 ? e.data.reason ?? '' : '';
}

// 抽屉按钮 / 行内直调写动作的宿主级在途锁（宿主 × 动作键；参照 pages/cards.ts guarded）：在途期间重复触发直接忽略，结束即释放
const inflight = new WeakMap<object, Set<string>>();

async function guarded(ctx: AdminCtx, key: string, fn: () => Promise<void>): Promise<void> {
  let keys = inflight.get(ctx.host);
  if (!keys) {
    keys = new Set();
    inflight.set(ctx.host, keys);
  }
  if (keys.has(key)) return;
  keys.add(key);
  const alive = sessionGuard(ctx);
  try {
    await fn();
  } catch (e) {
    if (alive()) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  } finally {
    keys.delete(key);
  }
}

// 抽屉二次取数（详情 / 登录记录）回填：仍是同一抽屉才替换，保留已输入的备注（工单回复输入）
function swapDrawer(ctx: AdminCtx, prev: DrawerSpec, next: DrawerSpec): void {
  const tb = ctx.host.state.tb;
  if (tb.drawer === prev) ctx.host.setState({ tb: { ...tb, drawer: next } });
}

// ---- C-20 公告 / 工单 / FAQ ----

const CMS_EDIT: Need = { module: 'cms', bit: 'edit' };
const CMS_KINDS = ['announcement', 'ticket', 'faq'] as const;
const CONTENT_ROUTE = 'GET /api/admin/contents/{id}';

function cmsStatusMap(t: Dict): Readonly<Record<string, readonly [ChipKind, string]>> {
  return { on: ['green', str(t, 'stOn')], off: ['gray', str(t, 'stOffline')], open: ['amber', str(t, 'stOpen')], resolved: ['gray', str(t, 'stResolved')] };
}

function cmsStatusText(t: Dict, v: string): string {
  return cmsStatusMap(t)[v]?.[1] || v;
}

// Type 选项显示「公告 / 工单 / FAQ」（06 C-20 表单表给出译文，取页签文案）
function kindLabel(t: Dict, k: string): string {
  const i = CMS_KINDS.findIndex((x) => x === k);
  return (i >= 0 ? strArr(t, 'tabCms')[i] : undefined) ?? k;
}

function contentTitle(ctx: AdminCtx, r: ContentRow): string {
  return ctx.lang === 'zh' ? r.title : r.title_en || r.title;
}

// 06 C-20 标题列 sub：公告 = 发布时间，FAQ = 排序 {sort_order}，工单 = biz_no
function contentSub(ctx: AdminCtx, r: ContentRow): string {
  if (r.kind === 'announcement') return dt(ctx, r.published_at);
  if (r.kind === 'faq') return `${str(ctx.t, 'cSortOrder')} ${r.sort_order}`;
  return r.biz_no;
}

function contentCells(ctx: AdminCtx, r: ContentRow): readonly CellVals[] {
  return [cell(contentTitle(ctx, r), contentSub(ctx, r)), cell(r.channel ?? EMPTY), cell(r.assignee_account ?? EMPTY), chipOf(cmsStatusMap(ctx.t), r.status, 'cms.status'), cell(dt(ctx, r.updated_at))];
}

async function contentDetail(ctx: AdminCtx, id: number): Promise<ContentDetail> {
  return ctx.gp.api.call<ContentDetail>(CONTENT_ROUTE, { path: { id } });
}

function cmsFormTitle(t: Dict, editing: boolean): string {
  return `${str(t, editing ? 'edit' : 'add')} · ${str(t, 'cms')}`;
}

// formDef cms（06 C-20 表单）。新增：kind 默认当前页签（不含 ticket，后端 POST 拒绝 ticket）；编辑：kind 只读（PUT 改 kind → 4220）。
// status 选项随 kind：announcement / faq → on / off，ticket → open / resolved；FAQ 才显示排序（字段集按表单当前 kind 取值）
function cmsForm(ctx: AdminCtx, kind: string, d: ContentDetail | null): FormDef {
  const t = ctx.t;
  const statusSel = (opts: readonly string[]): FieldDef => ({ k: 'status', label: str(t, 'cStatus'), type: 'select', options: opts, optionLabel: cmsStatusText });
  if (d && d.kind === 'ticket') {
    // 工单：后台只切换状态与处理人（TD-715），标题 / 正文为用户撰写，不改写
    return {
      title: cmsFormTitle(t, true),
      fields: [
        { k: 'title', label: str(t, 'cTitle'), type: 'readonly', wide: true },
        { k: 'kindText', label: 'Type', type: 'readonly' },
        { k: 'assignee_account', label: str(t, 'cAssignee'), type: 'text' },
        statusSel(['open', 'resolved']),
      ],
      init: { title: d.title, kindText: kindLabel(t, 'ticket'), assignee_account: d.assignee_account ?? '', status: d.status },
      submit: async (c, v) => {
        const alive = sessionGuard(c);
        await c.gp.api.call<ContentRow>('PUT /api/admin/contents/{id}', {
          path: { id: d.id },
          body: { kind: 'ticket', title: d.title, title_en: d.title_en ?? '', channel: d.channel ?? '', assignee_account: txt(v, 'assignee_account'), status: txt(v, 'status'), body: '', body_en: '' },
        });
        if (!alive()) return;
        c.gp.ui.toast(str(c.t, 'toastSaved'));
        void reloadCurrent(c);
      },
    };
  }
  const editing = d !== null;
  const base: readonly FieldDef[] = [
    { k: 'title', label: `${str(t, 'cTitle')} (中文)`, type: 'text', wide: true },
    { k: 'title_en', label: `${str(t, 'cTitle')} (EN)`, type: 'text', wide: true },
    editing ? { k: 'kindText', label: 'Type', type: 'readonly' } : { k: 'kind', label: 'Type', type: 'select', options: ['announcement', 'faq'], optionLabel: kindLabel },
    { k: 'channel', label: str(t, 'cChannel'), type: 'text' },
    { k: 'assignee_account', label: str(t, 'cAssignee'), type: 'text' },
    statusSel(['on', 'off']),
    { k: 'body', label: `${str(t, 'admBody')} (中文)`, type: 'text', wide: true },
    { k: 'body_en', label: `${str(t, 'admBody')} (EN)`, type: 'text', wide: true, optional: true },
  ];
  const sortField: FieldDef = { k: 'sort_order', label: str(t, 'cSortOrder'), type: 'number' };
  const init: FormVals = d
    ? { title: d.title, title_en: d.title_en ?? '', kindText: kindLabel(t, d.kind), channel: d.channel ?? '', assignee_account: d.assignee_account ?? '', status: d.status, body: d.body ?? '', body_en: d.body_en ?? '', sort_order: String(d.sort_order) }
    : { title: '', title_en: '', kind, channel: 'App', assignee_account: ctx.s.sh.me?.account ?? '', status: 'on', body: '', body_en: '', sort_order: '0' };
  const kindOf = (v: FormVals): string => (d ? d.kind : txt(v, 'kind'));
  const def: FormDef = {
    title: cmsFormTitle(t, editing),
    // 字段集随表单当前 kind（FAQ 显示排序，其余隐藏）：table 域每次渲染与必填校验都读 def.fields
    get fields(): readonly FieldDef[] {
      const cur = ctx.host.state.tb.form;
      const vals = cur && cur.def === def ? cur.vals : init;
      return kindOf(vals) === 'faq' ? [...base, sortField] : base;
    },
    init,
    validate: (v, dict) => (kindOf(v) === 'faq' && !/^\d+$/.test(txt(v, 'sort_order')) ? str(dict, 'admErrSortOrder') : ''),
    submit: async (c, v) => {
      const k = kindOf(v);
      const body = {
        kind: k,
        title: txt(v, 'title'),
        title_en: txt(v, 'title_en'),
        channel: txt(v, 'channel'),
        assignee_account: txt(v, 'assignee_account'),
        status: txt(v, 'status'),
        body: typeof v['body'] === 'string' ? v['body'] : '',
        body_en: typeof v['body_en'] === 'string' ? v['body_en'] : '',
        ...(k === 'faq' ? { sort_order: Number(txt(v, 'sort_order')) } : {}),
      };
      const alive = sessionGuard(c);
      if (d) await c.gp.api.call<ContentRow>('PUT /api/admin/contents/{id}', { path: { id: d.id }, body });
      else await c.gp.api.call<ContentRow>('POST /api/admin/contents', { body });
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, d ? 'toastSaved' : 'toastCreated'));
      void reloadCurrent(c);
    },
  };
  return def;
}

async function editContent(ctx: AdminCtx, id: number): Promise<void> {
  const alive = sessionGuard(ctx);
  const d = await contentDetail(ctx, id);
  if (!alive()) return;
  openForm(ctx, cmsForm(ctx, d.kind, d));
}

// 06 C-20 工单「回复」：备注框非空才发；成功 toast 并重拉详情（换抽屉即清空输入）
async function replyTicket(ctx: AdminCtx, r: ContentRow): Promise<void> {
  const content = ctx.host.state.tb.note.trim();
  if (content === '') return;
  await guarded(ctx, `reply:${r.id}`, async () => {
    const alive = sessionGuard(ctx);
    await ctx.gp.api.call('POST /api/admin/contents/{id}/reply', { path: { id: r.id }, body: { content } });
    if (!alive()) return;
    ctx.gp.ui.toast(str(ctx.t, 'toastReplied'));
    const spec = ctx.host.state.tb.drawer;
    const d = await contentDetail(ctx, r.id);
    if (!alive() || !spec || ctx.host.state.tb.drawer !== spec) return;
    openDrawer(ctx, contentDrawer(ctx, r, d));
  });
}

function threadSub(ctx: AdminCtx, m: { sender: string; sender_label: string; created_at: string }): string {
  const t = ctx.t;
  const who = m.sender === 'admin' ? interpolate(str(t, 'senderAdmin'), { account: m.sender_label }) : `${str(t, 'senderUser')} · ${m.sender_label}`;
  return `${who} · ${dt(ctx, m.created_at)}`;
}

function refTxText(d: ContentDetail | null): string {
  const x = d?.ref_tx;
  if (!x) return EMPTY;
  return `#${x.id} · ${x.merchant_name ?? EMPTY} · ${x.billing_amount ?? EMPTY}`;
}

function contentDrawer(ctx: AdminCtx, r: ContentRow, d: ContentDetail | null): DrawerSpec {
  const t = ctx.t;
  const ticket = r.kind === 'ticket';
  const fields: Array<{ k: string; v: string; wide?: boolean }> = [
    { k: str(t, 'cChannel'), v: r.channel ?? EMPTY },
    { k: str(t, 'cAssignee'), v: r.assignee_account ?? EMPTY },
    { k: str(t, 'cStatus'), v: cmsStatusText(t, r.status) },
    { k: str(t, 'cUpdated'), v: dt(ctx, r.updated_at) },
  ];
  if (r.kind === 'announcement') fields.push({ k: str(t, 'admPublishedAt'), v: dt(ctx, r.published_at) });
  const user = d?.user ?? null;
  if (ticket) {
    fields.push({ k: str(t, 'admSubmitter'), v: user ? `${user.uid} · ${user.email_masked ?? EMPTY}` : EMPTY, wide: true });
    fields.push({ k: str(t, 'admRefTx'), v: refTxText(d), wide: true });
  }
  const edit: DrawerAction = {
    label: 'aEdit',
    kind: 'primary',
    need: CMS_EDIT,
    run: async (c) => {
      closeDrawer(c);
      await editContent(c, r.id);
    },
  };
  if (!ticket) return { kind: str(t, 'cms'), title: contentTitle(ctx, r), fields, actions: [edit] };
  const thread = [...(d?.thread ?? [])].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  const actions: DrawerAction[] = [];
  if (user) actions.push({ label: 'aOpen360', kind: '', run: (c) => openUser360(c, user.id) });
  actions.push({
    label: 'aReply',
    kind: 'green',
    need: CMS_EDIT,
    // 06 C-20：备注框非空才可点（置灰提示取 replyPh）
    gate: (c) => (c.s.tb.note.trim() === '' ? { disabled: true, title: str(c.t, 'replyPh') } : { disabled: false, title: '' }),
    run: (c) => replyTicket(c, r),
  });
  actions.push(edit);
  return {
    kind: str(t, 'cms'),
    title: contentTitle(ctx, r),
    subtitle: r.biz_no,
    fields,
    list: { title: str(t, 'ticketThread'), rows: thread.map((m) => ({ left: m.body, sub: threadSub(ctx, m) })) },
    note: true,
    actions,
  };
}

function openContent(ctx: AdminCtx, r: ContentRow): void {
  const spec = contentDrawer(ctx, r, null);
  openDrawer(ctx, spec);
  const alive = sessionGuard(ctx);
  void contentDetail(ctx, r.id)
    .then((d) => {
      if (alive()) swapDrawer(ctx, spec, contentDrawer(ctx, r, d));
    })
    .catch((e: unknown) => {
      if (alive() && ctx.host.state.tb.drawer === spec) ctx.gp.ui.toast(ctx.gp.ui.err(e));
    });
}

function deleteContent(ctx: AdminCtx, r: ContentRow): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'delTitle'),
    text: str(t, 'delText'),
    label: contentTitle(ctx, r),
    run: async (c) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('DELETE /api/admin/contents/{id}', { path: { id: r.id } });
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, 'toastDeleted'));
      await reloadCurrent(c);
    },
  });
}

function cmsView(kind: (typeof CMS_KINDS)[number]): ErasedView {
  return defineView<ContentRow>({
    key: kind,
    source: { route: 'GET /api/admin/contents', tabParam: 'tab' },
    echo: '/admin/contents',
    cols: [{ label: 'cTitle' }, { label: 'cChannel' }, { label: 'cAssignee' }, { label: 'cStatus', facet: 'status', facetLabel: cmsStatusText }, { label: 'cUpdated', sortKey: 'updated_at' }],
    cells: contentCells,
    open: openContent,
    rowActions: (): readonly ActionDef<ContentRow>[] => [
      { label: 'aEdit', need: CMS_EDIT, run: (c, r) => editContent(c, r.id) },
      { label: 'aDelete', need: CMS_EDIT, danger: true, run: (c, r) => deleteContent(c, r) },
    ],
    // 新增：kind 默认当前页签；工单页签下回落公告（工单只由用户端创建）
    primary: { label: 'newAnnouncement', icon: '+ ', need: CMS_EDIT, run: (c) => openForm(c, cmsForm(c, kind === 'ticket' ? 'announcement' : kind, null)) },
    csvName: 'cms',
  });
}

// ---- C-21 管理员账号 ----

const ADMIN_EDIT: Need = { module: 'admins', bit: 'edit' };

function acctStatusMap(t: Dict): Readonly<Record<string, readonly [ChipKind, string]>> {
  return { active: ['green', str(t, 'stOn')], disabled: ['red', str(t, 'stOff')] };
}

function acctStatusText(t: Dict, v: string): string {
  return acctStatusMap(t)[v]?.[1] || v;
}

// 06 C-21：2FA 0 → 未绑定 amber（2FA 强制，0 仅出现在首登前或被重置后）
function twoFaCell(t: Dict, on: boolean): CellVals {
  return on ? chip('green', str(t, 'stOn')) : chip('amber', str(t, 'st2faUnbound'));
}

function adminCells(ctx: AdminCtx, a: AdminAcctRow): readonly CellVals[] {
  const t = ctx.t;
  return [
    cell(a.account, a.is_self ? str(t, 'selfTip') : String(a.id), true),
    cell(a.name),
    cell(a.role.name),
    twoFaCell(t, a.totp_enabled),
    cell(dt(ctx, a.last_login_at), a.last_login_ip ?? ''),
    cell(dt(ctx, a.created_at)),
    chipOf(acctStatusMap(t), a.status, 'admins.status'),
  ];
}

function count(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// B3 summary：形状逐项校验，缺失或非数字 → null（统计卡显示「—」，不以当前页数据冒充全量）
export function summaryOf(page: Readonly<Record<string, unknown>>): Partial<AcctSummary> {
  const s = page['summary'];
  if (typeof s !== 'object' || s === null) return {};
  const pick = (k: keyof AcctSummary): number | null => count(Reflect.get(s, k));
  const out: Partial<AcctSummary> = {};
  const total = pick('total');
  const active = pick('active');
  const totpOn = pick('totp_on');
  if (total !== null) out.total = total;
  if (active !== null) out.active = active;
  if (totpOn !== null) out.totp_on = totpOn;
  return out;
}

// C-21 重置 2FA：非本人且已绑定（06 行内动作显示条件；服务端 1501 兜底）
export function canResetTwoFa(a: Pick<AdminAcctRow, 'is_self' | 'totp_enabled'>): boolean {
  return !a.is_self && a.totp_enabled;
}

export function resetTwoFa(ctx: AdminCtx, a: Pick<AdminAcctRow, 'id' | 'account' | 'name' | 'is_self' | 'totp_enabled'>): void {
  if (!canResetTwoFa(a)) return;
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'reset2faTitle'),
    text: str(t, 'reset2faText'),
    label: `${a.account} · ${a.name}`,
    run: async (c) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/accounts/{id}/reset-2fa', { path: { id: a.id }, body: {} });
      // 会话已换（session:lost / ready）时不向下一账号提示上一账号的操作结果
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, 'toastReset2fa'));
      await reloadCurrent(c);
    },
  });
}

interface RoleChoice {
  id: number;
  name: string;
}

function isAcctRow(v: unknown): v is AdminAcctRow {
  if (typeof v !== 'object' || v === null) return false;
  const role: unknown = Reflect.get(v, 'role');
  return typeof role === 'object' && role !== null && typeof Reflect.get(role, 'id') === 'number' && typeof Reflect.get(role, 'name') === 'string';
}

// 角色下拉：GET /roles 为超管专属（矩阵外）；非超管取当前列表行中出现过的角色（服务端 1501 兜底越权指派）
async function roleChoices(ctx: AdminCtx): Promise<readonly RoleChoice[]> {
  if (ctx.s.sh.me?.role === SUPER_ADMIN_ROLE) {
    const list = await ctx.gp.api.call<RoleRow[]>('GET /api/admin/roles');
    return Array.isArray(list) ? list.map((r) => ({ id: r.id, name: r.name })) : [];
  }
  const v = viewOf(ctx);
  const rows = v ? tableOf(ctx, ctx.s.page, v).rows : [];
  const seen = new Map<number, string>();
  for (const r of rows) if (isAcctRow(r)) seen.set(r.role.id, r.role.name);
  return [...seen].map(([id, name]) => ({ id, name }));
}

function lastSuperErr(route: Parameters<typeof localErr>[0], e: unknown, t: Dict): unknown {
  return reasonOf(e) === 'last_super_admin' ? localErr(route, str(t, 'admErrLastSuper')) : e;
}

// formDef admin（06 C-21；P6：无初始密码 / 状态 / 编辑态重置密码字段——后端 POST 只收 {account,name,email,role_id}、PUT 只收 {name,email,role_id}）
function adminForm(ctx: AdminCtx, a: AdminAcctRow | null, roles: readonly RoleChoice[]): FormDef {
  const t = ctx.t;
  const names = roles.map((r) => r.name);
  const roleOf = (name: string): RoleChoice | undefined => roles.find((r) => r.name === name);
  const fallback = roles.find((r) => /support/i.test(r.name)) ?? roles[roles.length - 1];
  // 本人改角色本地禁用（服务端改自己 role_id → 1501）
  const roleField: FieldDef = a?.is_self ? { k: 'role', label: str(t, 'cRole'), type: 'readonly' } : { k: 'role', label: str(t, 'cRole'), type: 'select', options: names };
  return {
    title: `${str(t, a ? 'edit' : 'add')} · ${str(t, 'admins')}`,
    ...(a ? {} : { tip: str(t, 'adminCreateTip') }),
    fields: [
      { k: 'account', label: str(t, 'cAdminAcct'), type: a ? 'readonly' : 'text' },
      { k: 'name', label: str(t, 'cAdminName'), type: 'text' },
      { k: 'email', label: str(t, 'cEmail2'), type: 'text', placeholder: 'name@goodpayu.com' },
      roleField,
    ],
    init: { account: a?.account ?? '', name: a?.name ?? '', email: a?.email ?? '', role: a?.role.name ?? fallback?.name ?? '' },
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      if (!a) {
        const route = 'POST /api/admin/accounts';
        const role = roleOf(txt(v, 'role'));
        if (!role) throw localErr(route, `${str(c.t, 'required')}： ${str(c.t, 'cRole')}`);
        await c.gp.api.call<AdminAcctRow>(route, { body: { account: txt(v, 'account'), name: txt(v, 'name'), email: txt(v, 'email'), role_id: role.id } });
        if (!alive()) return;
        c.gp.ui.toast(str(c.t, 'toastAdminCreated'));
      } else {
        const route = 'PUT /api/admin/accounts/{id}';
        const roleId = a.is_self ? a.role.id : roleOf(txt(v, 'role'))?.id;
        if (roleId === undefined) throw localErr(route, `${str(c.t, 'required')}： ${str(c.t, 'cRole')}`);
        try {
          await c.gp.api.call<AdminAcctRow>(route, { path: { id: a.id }, body: { name: txt(v, 'name'), email: txt(v, 'email'), role_id: roleId } });
        } catch (e) {
          throw lastSuperErr(route, e, c.t);
        }
        if (!alive()) return;
        c.gp.ui.toast(str(c.t, 'toastSaved'));
      }
      void reloadCurrent(c);
    },
  };
}

async function openAdminForm(ctx: AdminCtx, a: AdminAcctRow | null): Promise<void> {
  const alive = sessionGuard(ctx);
  const roles = await roleChoices(ctx);
  if (!alive()) return;
  openForm(ctx, adminForm(ctx, a, roles));
}

const PWD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,20}$/;

// formDef adminPwd（06 C-21）：tip 末尾插收件邮箱
function adminPwdForm(ctx: AdminCtx, a: AdminAcctRow): FormDef {
  const t = ctx.t;
  const tip = str(t, 'adminPwdTip');
  return {
    title: `${str(t, 'resetPwd')} · ${a.account}`,
    tip: tip.includes('{email}') ? interpolate(tip, { email: a.email }) : `${tip}${a.email}`,
    fields: [{ k: 'password', label: str(t, 'password'), type: 'password', wide: true, placeholder: '••••••••' }],
    init: { password: '' },
    validate: (v, d) => (PWD_RULE.test(typeof v['password'] === 'string' ? v['password'] : '') ? '' : str(d, 'admErrPwdPolicy')),
    submit: async (c, v) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('POST /api/admin/accounts/{id}/reset-password', { path: { id: a.id }, body: { password: typeof v['password'] === 'string' ? v['password'] : '' } });
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, 'toastAdminPwd'));
    },
  };
}

// 停用 / 启用（行内与抽屉共用；1012 last_super_admin → admErrLastSuper）
async function toggleAdmin(ctx: AdminCtx, a: AdminAcctRow): Promise<void> {
  const alive = sessionGuard(ctx);
  try {
    await ctx.gp.api.call<AdminAcctRow>('POST /api/admin/accounts/{id}/toggle', { path: { id: a.id }, body: {} });
  } catch (e) {
    if (!alive()) return;
    if (reasonOf(e) === 'last_super_admin') {
      ctx.gp.ui.toast(str(ctx.t, 'admErrLastSuper'));
      return;
    }
    throw e;
  }
  if (!alive()) return;
  ctx.gp.ui.toast(str(ctx.t, a.status === 'active' ? 'toastAdminOff' : 'toastAdminOn'));
  await reloadCurrent(ctx);
}

function deleteAdmin(ctx: AdminCtx, a: AdminAcctRow): void {
  const t = ctx.t;
  openConfirm(ctx, {
    title: str(t, 'delTitle'),
    text: str(t, 'delText'),
    label: `${a.account} · ${a.name}`,
    run: async (c) => {
      const alive = sessionGuard(c);
      try {
        await c.gp.api.call('DELETE /api/admin/accounts/{id}', { path: { id: a.id } });
      } catch (e) {
        throw lastSuperErr('DELETE /api/admin/accounts/{id}', e, c.t);
      }
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, 'toastDeleted'));
      await reloadCurrent(c);
    },
  });
}

function adminDrawer(ctx: AdminCtx, a: AdminAcctRow, logs: readonly LoginLogRow[]): DrawerSpec {
  const t = ctx.t;
  const fields: Array<{ k: string; v: string; wide?: boolean }> = [
    { k: str(t, 'cAdminName'), v: a.name },
    { k: str(t, 'cEmail2'), v: a.email || EMPTY },
    { k: str(t, 'cRole'), v: a.role.name },
    { k: '2FA', v: str(t, a.totp_enabled ? 'stOn' : 'st2faUnbound') },
    { k: str(t, 'cStatus'), v: acctStatusText(t, a.status) },
    { k: str(t, 'cLastLogin'), v: dt(ctx, a.last_login_at) },
    { k: str(t, 'cLoginIp'), v: a.last_login_ip ?? EMPTY },
    { k: str(t, 'cCreated'), v: dt(ctx, a.created_at), wide: true },
  ];
  if (a.is_self) fields.push({ k: str(t, 'cStatus'), v: str(t, 'selfTip'), wide: true });
  const actions: DrawerAction[] = [{ label: 'resetPwd', kind: '', need: ADMIN_EDIT, run: (c) => openForm(c, adminPwdForm(c, a)) }];
  if (canResetTwoFa(a)) {
    actions.push({
      label: 'reset2faTitle',
      kind: '',
      need: ADMIN_EDIT,
      run: (c) => {
        closeDrawer(c);
        resetTwoFa(c, a);
      },
    });
  }
  if (!a.is_self) {
    actions.push({
      label: a.status === 'active' ? 'stOff' : 'stOn',
      kind: a.status === 'active' ? 'danger' : 'green',
      need: ADMIN_EDIT,
      run: (c) => guarded(c, `toggle:${a.id}`, async () => {
        closeDrawer(c);
        await toggleAdmin(c, a);
      }),
    });
  }
  actions.push({
    label: 'aEdit',
    kind: 'primary',
    need: ADMIN_EDIT,
    run: async (c) => {
      closeDrawer(c);
      await openAdminForm(c, a);
    },
  });
  return {
    kind: str(t, 'admins'),
    title: a.account,
    subtitle: `${a.name} · ${a.role.name}`,
    fields,
    // 时间线 = 登录记录（admin_login_logs：{成功|失败} · {UA} · {IP} + 时间）
    timeline: logs.map((l) => ({ label: `${str(t, l.success ? 'stSuccess' : 'stFailed')} · ${l.user_agent ?? EMPTY} · ${l.ip}`, time: dt(ctx, l.created_at) })),
    actions,
  };
}

function openAdmin(ctx: AdminCtx, a: AdminAcctRow): void {
  const spec = adminDrawer(ctx, a, []);
  openDrawer(ctx, spec);
  const alive = sessionGuard(ctx);
  void ctx.gp.api
    .call<LoginLogRow[]>('GET /api/admin/accounts/{id}/login-logs', { path: { id: a.id } })
    .then((logs) => {
      if (alive()) swapDrawer(ctx, spec, adminDrawer(ctx, a, Array.isArray(logs) ? logs : []));
    })
    .catch((e: unknown) => {
      if (alive()) console.warn('[gp:admin] 登录记录读取失败', a.id, e);
    });
}

function adminView(key: 'all' | 'active' | 'disabled'): ErasedView {
  return defineView<AdminAcctRow>({
    key,
    source: { route: 'GET /api/admin/accounts', tabParam: 'tab' },
    echo: '/admin/accounts',
    cols: [
      { label: 'cAdminAcct' },
      { label: 'cAdminName' },
      { label: 'cRole', facet: 'role' },
      { label: 'c2fa' },
      { label: 'cLastLogin', sortKey: 'last_login_at' },
      { label: 'cCreated', sortKey: 'created_at' },
      { label: 'cStatus', facet: 'status', facetLabel: acctStatusText },
    ],
    cells: adminCells,
    open: openAdmin,
    rowActions: (): readonly ActionDef<AdminAcctRow>[] => [
      { label: 'aEdit', need: ADMIN_EDIT, run: (c, a) => openAdminForm(c, a) },
      { label: 'resetPwd', need: ADMIN_EDIT, run: (c, a) => openForm(c, adminPwdForm(c, a)) },
      { label: 'reset2faTitle', need: ADMIN_EDIT, show: canResetTwoFa, run: (c, a) => resetTwoFa(c, a) },
      // 行内直调写动作：与抽屉同一宿主 × 动作键在途锁（toggle:{id}）
      { label: 'stOff', need: ADMIN_EDIT, danger: true, show: (a) => !a.is_self && a.status === 'active', run: (c, a) => guarded(c, `toggle:${a.id}`, () => toggleAdmin(c, a)) },
      { label: 'stOn', need: ADMIN_EDIT, show: (a) => !a.is_self && a.status !== 'active', run: (c, a) => guarded(c, `toggle:${a.id}`, () => toggleAdmin(c, a)) },
      { label: 'aDelete', need: ADMIN_EDIT, danger: true, show: (a) => !a.is_self, run: (c, a) => deleteAdmin(c, a) },
    ],
    // 06 C-21 统计卡：spec §4.5 summary（全表计数，B3）；缺失「—」
    stats: (ctx, page) => {
      const t = ctx.t;
      const s = summaryOf(page);
      const n = (v: number | undefined): string => (v === undefined ? EMPTY : String(v));
      return [
        { label: str(t, 'admins'), value: n(s.total), sub: str(t, 'gSystem'), dark: true },
        { label: str(t, 'stOn'), value: n(s.active), sub: str(t, 'cStatus') },
        { label: '2FA', value: s.totp_on === undefined || s.total === undefined ? EMPTY : `${s.totp_on}/${s.total}`, sub: str(t, 'twofaSub') },
      ];
    },
    primary: { label: 'newAdmin', icon: '+ ', need: ADMIN_EDIT, run: (c) => openAdminForm(c, null) },
    csvName: 'admins',
  });
}

export const PAGES_PLATFORM: PageMap = {
  cms: { page: 'cms', tabLabels: (t) => strArr(t, 'tabCms'), views: CMS_KINDS.map(cmsView), i18n: I18N_PLATFORM, rewrites: REWRITES_PLATFORM },
  admins: { page: 'admins', tabLabels: (t) => strArr(t, 'tabAdmins'), views: (['all', 'active', 'disabled'] as const).map(adminView), i18n: I18N_PLATFORM, rewrites: REWRITES_PLATFORM },
};

// ---- C-13 风控限额 / 命中记录（products 页 Tab1 / Tab2） ----
// riskViews 在 products.ts 模块求值期被调用（加载环中本模块可能尚未求值完）：构造路径只用函数声明与函数内常量，不读本模块顶层 const

function isBuiltin(r: RiskRuleRow): boolean {
  return r.hard_coded_key !== null && r.hard_coded_key !== '';
}

// 内置规则锚定的系统参数（06 C-13「一期硬执行 4 条」；daily_open_limit 为 0051 新增）
function anchorOf(key: string): string {
  if (key === 'max_cards') return 'maxCards';
  if (key === 'kyc_limit') return 'kycLimit / kycLimitVerified';
  if (key === 'withdraw_review') return 'withdrawReviewThreshold';
  if (key === 'daily_open_limit') return 'maxOpenPerDay';
  return key;
}

function ruleName(ctx: AdminCtx, r: RiskRuleRow): string {
  return ctx.lang === 'zh' ? r.name : r.name_en || r.name;
}

function ruleThreshold(r: RiskRuleRow): string {
  return isBuiltin(r) ? r.threshold_live ?? r.threshold : r.threshold;
}

function ruleCells(ctx: AdminCtx, r: RiskRuleRow): readonly CellVals[] {
  const t = ctx.t;
  return [
    cell(ruleName(ctx, r), isBuiltin(r) ? `${r.biz_no} · ${str(t, 'ruleBuiltin')}` : r.biz_no),
    cell(r.scope),
    cell(ruleThreshold(r)),
    cell(r.action),
    cell(dt(ctx, r.created_at)),
    r.enabled ? chip('green', str(t, 'stOn')) : chip('gray', str(t, 'stOff')),
  ];
}

// formDef rule（06 C-13 Tab1）。内置规则：阈值 / 触发动作只读 + ruleAnchorTip，启用置灰恒开（ruleAlwaysOn）；
// PUT 整体提交，内置规则阈值 / 动作按库值原样回送（服务端改动即 1501）
function ruleForm(ctx: AdminCtx, r: RiskRuleRow | null): FormDef {
  const t = ctx.t;
  const builtin = r !== null && isBuiltin(r);
  const fields: FieldDef[] = [
    { k: 'name', label: `${str(t, 'cRule')} (中文)`, type: 'text' },
    { k: 'name_en', label: `${str(t, 'cRule')} (EN)`, type: 'text' },
    { k: 'scope', label: str(t, 'cScope'), type: 'text' },
    { k: 'threshold', label: str(t, 'cValue'), type: builtin ? 'readonly' : 'text' },
    { k: 'action', label: str(t, 'cAction'), type: builtin ? 'readonly' : 'text' },
    builtin ? { k: 'enabledText', label: str(t, 'cEnabled'), type: 'readonly' } : { k: 'enabled', label: str(t, 'cEnabled'), type: 'switch' },
  ];
  const tip = builtin && r ? `${interpolate(str(t, 'ruleAnchorTip'), { key: anchorOf(r.hard_coded_key ?? '') })}；${str(t, 'ruleAlwaysOn')}` : '';
  return {
    title: `${str(t, r ? 'edit' : 'add')} · ${str(t, 'cRule')}`,
    ...(tip ? { tip } : {}),
    fields,
    init: r
      ? { name: r.name, name_en: r.name_en ?? '', scope: r.scope, threshold: ruleThreshold(r), action: r.action, enabled: r.enabled, enabledText: `${str(t, 'stOn')} · ${str(t, 'ruleAlwaysOn')}` }
      : { name: '', name_en: '', scope: '', threshold: '', action: '', enabled: true },
    submit: async (c, v) => {
      const body = {
        name: txt(v, 'name'),
        name_en: txt(v, 'name_en'),
        scope: txt(v, 'scope'),
        threshold: builtin && r ? r.threshold : txt(v, 'threshold'),
        action: builtin && r ? r.action : txt(v, 'action'),
        enabled: builtin ? true : v['enabled'] === true,
      };
      const alive = sessionGuard(c);
      if (r) await c.gp.api.call<RiskRuleRow>('PUT /api/admin/risk-rules/{id}', { path: { id: r.id }, body });
      else await c.gp.api.call<RiskRuleRow>('POST /api/admin/risk-rules', { body });
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, r ? 'toastSaved' : 'toastCreated'));
      void reloadCurrent(c);
    },
  };
}

// 停用 / 启用（非内置）：PUT 整体提交，只翻转 enabled
async function toggleRule(ctx: AdminCtx, r: RiskRuleRow): Promise<void> {
  if (isBuiltin(r)) return;
  const alive = sessionGuard(ctx);
  await ctx.gp.api.call<RiskRuleRow>('PUT /api/admin/risk-rules/{id}', {
    path: { id: r.id },
    body: { name: r.name, name_en: r.name_en ?? '', scope: r.scope, threshold: r.threshold, action: r.action, enabled: !r.enabled },
  });
  if (!alive()) return;
  ctx.gp.ui.toast(str(ctx.t, 'toastSaved'));
  await reloadCurrent(ctx);
}

function deleteRule(ctx: AdminCtx, r: RiskRuleRow): void {
  if (isBuiltin(r)) return;
  openConfirm(ctx, {
    title: str(ctx.t, 'delTitle'),
    text: str(ctx.t, 'delText'),
    label: ruleName(ctx, r),
    run: async (c) => {
      const alive = sessionGuard(c);
      await c.gp.api.call('DELETE /api/admin/risk-rules/{id}', { path: { id: r.id } });
      if (!alive()) return;
      c.gp.ui.toast(str(c.t, 'toastDeleted'));
      await reloadCurrent(c);
    },
  });
}

function hitRuleName(ctx: AdminCtx, h: RiskHitRow): string {
  const zh = h.rule_name ?? h.rule_key;
  return ctx.lang === 'zh' ? zh : h.rule_name_en || zh;
}

function hitUser(h: RiskHitRow): string {
  return h.user.email ?? h.user.uid;
}

// 上下文摘要列「超长省略」（06 C-13 Tab2）：单元格不换行，按字符截断
function clip(s: string, n: number): string {
  const cs = [...s];
  return cs.length > n ? `${cs.slice(0, n).join('')}…` : s;
}

function hitCells(ctx: AdminCtx, h: RiskHitRow): readonly CellVals[] {
  return [cell(dt(ctx, h.created_at)), cell(hitRuleName(ctx, h), h.rule_key), cell(hitUser(h), h.user.email ? h.user.uid : ''), cell(h.action, '', true), cell(clip(h.context_summary, 48))];
}

function openHit(ctx: AdminCtx, h: RiskHitRow): void {
  const t = ctx.t;
  openDrawer(ctx, {
    kind: str(t, 'tabHits'),
    title: hitRuleName(ctx, h),
    subtitle: hitUser(h),
    fields: [
      { k: str(t, 'cTime'), v: dt(ctx, h.created_at) },
      { k: str(t, 'cRule'), v: `${hitRuleName(ctx, h)} · ${h.rule_key}` },
      { k: str(t, 'cUser'), v: `${hitUser(h)} · ${h.user.uid}` },
      { k: str(t, 'cHitAction'), v: h.action },
    ],
    json: JSON.stringify(h.context ?? null, null, 2),
    jsonTitle: str(t, 'cContext'),
    actions: [
      { label: 'aOpen360', kind: '', run: (c) => openUser360(c, h.user.id) },
      { label: 'aClose', kind: 'primary', run: (c) => closeDrawer(c) },
    ],
  });
}

export function riskViews(): readonly ErasedView[] {
  const edit: Need = { module: 'products', bit: 'edit' };
  const rulesView = defineView<RiskRuleRow>({
    key: 'rules',
    source: { route: 'GET /api/admin/risk-rules', tabParam: 'none' },
    echo: '/admin/risk-rules',
    cols: [{ label: 'cRule' }, { label: 'cScope' }, { label: 'cValue' }, { label: 'cAction' }, { label: 'cCreated', sortKey: 'created_at' }, { label: 'cEnabled', facet: 'enabled', facetLabel: enabledLabel }],
    cells: ruleCells,
    // 06 C-13：行点击直接开编辑表单（无抽屉）
    open: (c, r) => openForm(c, ruleForm(c, r)),
    rowActions: (): readonly ActionDef<RiskRuleRow>[] => [
      { label: 'aEdit', need: edit, run: (c, r) => openForm(c, ruleForm(c, r)) },
      // 06 C-13 Tab1 行内动作：只有删除为 danger（与原型 L778 同）
      { label: 'stOff', need: edit, show: (r) => !isBuiltin(r) && r.enabled, run: (c, r) => guarded(c, `rule:${r.id}`, () => toggleRule(c, r)) },
      { label: 'stOn', need: edit, show: (r) => !isBuiltin(r) && !r.enabled, run: (c, r) => guarded(c, `rule:${r.id}`, () => toggleRule(c, r)) },
      // 内置规则保护：删除按钮隐藏（服务端 1501 兜底）
      { label: 'aDelete', need: edit, danger: true, show: (r) => !isBuiltin(r), run: (c, r) => deleteRule(c, r) },
    ],
    primary: { label: 'newRule', icon: '+ ', need: edit, run: (c) => openForm(c, ruleForm(c, null)) },
    csvName: 'products-rules',
  });
  const hitsView = defineView<RiskHitRow>({
    key: 'hits',
    source: { route: 'GET /api/admin/risk-hits', tabParam: 'none' },
    echo: '/admin/risk-hits',
    cols: [{ label: 'cTime', sortKey: 'created_at' }, { label: 'cRule', facet: 'rule_key' }, { label: 'cUser' }, { label: 'cHitAction' }, { label: 'cContext' }],
    cells: hitCells,
    open: openHit,
    csvName: 'products-hits',
  });
  return [rulesView, hitsView];
}
